import { generateId } from "@fleet/utils";
/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate (technical debt) */
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, onCleanup, Show, untrack } from "solid-js";
import { chronosRoute } from "../chronos";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { Battery, Brain, Pause, Play, RotateCcw, Zap } from "@fleet/icons";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import {
  and,
  eq,
  feed_event,
  focus_session,
  type FocusSession,
  pomodoro_config,
  type PomodoroConfig,
  sql,
} from "@time-tracker/powersync";
import { showToast } from "@/components/ui/toast";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import {
  cn,
  liveFocusSessionFilters,
  livePomodoroConfigFilters,
  patchRowVerified,
  queryKeys,
} from "@time-tracker/shared";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SECONDS_PER_MINUTE = 60;
const TIMER_INTERVAL_MS = 1000;
const PADDING_NUM_WIDTH = 2;

const FOCUS_PRESET_15 = 15;
const FOCUS_PRESET_25 = 25;
const FOCUS_PRESET_45 = 45;
const FOCUS_PRESET_60 = 60;
const FOCUS_PRESET_MINUTES = [FOCUS_PRESET_15, FOCUS_PRESET_25, FOCUS_PRESET_45, FOCUS_PRESET_60];

const BREAK_PRESET_5 = 5;
const BREAK_PRESET_10 = 10;
const BREAK_PRESET_15 = 15;
const BREAK_PRESET_MINUTES = [BREAK_PRESET_5, BREAK_PRESET_10, BREAK_PRESET_15];

const FATIGUE_THRESHOLD = 30;

const RETROSPECTIVE_HOURS = 12;
const MINUTES_IN_HOUR = 60;
const MS_PER_SECOND = 1000;

const MAX_ENERGY_LEVEL = 100;
const MIN_FOCUS_CAPACITY = 30;
const FOCUS_DECREMENT = 15;
const BREAK_INCREMENT = 10;

const SEGMENT_1 = 1;
const SEGMENT_2 = 2;
const SEGMENT_3 = 3;
const SEGMENT_4 = 4;
const POMODORO_SEGMENTS = [SEGMENT_1, SEGMENT_2, SEGMENT_3, SEGMENT_4];

const MIN_COGNITIVE_RESERVES = 20;
const COGNITIVE_FOCUS_DECREMENT = 10;
const COGNITIVE_BREAK_INCREMENT = 5;

const FATIGUE_INCREMENT = 12;
const FATIGUE_DECREMENT = 8;

function formatMinutesSeconds(totalSeconds: number) {
  const mins = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const secs = totalSeconds % SECONDS_PER_MINUTE;
  return `${mins.toString().padStart(PADDING_NUM_WIDTH, "0")}:${secs.toString().padStart(PADDING_NUM_WIDTH, "0")}`;
}

function DeepWorkPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { t } = useI18n();

  // Deep link: mode (focus/break) and focus/break presets travel in
  // `?mode=break&focus=45&break=10`, and the note in `?dialog=note`. This allows sharing
  // a focus configuration that reopens identically in another browser. URL→signal on
  // mount; signal→URL when toggling/choosing a preset (replace). Defaults are omitted
  // (focus mode, pomodoro_config presets) for clean links.
  const search = useSearch({ from: chronosDeepWorkRoute.id });
  const navigate = useNavigate();

  const [focusMinutes, setFocusMinutes] = createSignal(search().focus ?? FOCUS_PRESET_25);
  const [breakMinutes, setBreakMinutes] = createSignal(search().break ?? BREAK_PRESET_5);
  const [isBreak, setIsBreak] = createSignal(search().mode === "break");
  const [activeSessionId, setActiveSessionId] = createSignal<string | null>(null);
  const [currentSegment, setCurrentSegment] = createSignal(1);
  const [showAbortModal, setShowAbortModal] = createSignal(false);
  const [isNoteOpen, setIsNoteOpen] = createSignal(search().dialog === "note");
  const [noteText, setNoteText] = createSignal("");
  const [isSavingNote, setIsSavingNote] = createSignal(false);
  const { activeWorkspaceId: workspaceId } = useWorkspace();

  // Writes to the URL while preserving the remaining params (e.g. `?ws=`). Omits
  // default values to keep the link clean.
  const syncDeepWorkUrl = () => {
    void navigate({
      to: chronosDeepWorkRoute.to,
      search: (prev) => ({
        ...prev,
        mode: isBreak() ? ("break" as const) : undefined,
        focus: focusMinutes() === FOCUS_PRESET_25 ? undefined : focusMinutes(),
        break: breakMinutes() === BREAK_PRESET_5 ? undefined : breakMinutes(),
        dialog: isNoteOpen() ? ("note" as const) : undefined,
      }),
      replace: true,
    });
  };

  // URL → signals: back/forward or a pasted link. Idempotent on reload. We do not touch
  // the timer if a session is running (active state overrides the preselection).
  createEffect(() => {
    const urlMode = search().mode === "break";
    const urlFocus = search().focus ?? FOCUS_PRESET_25;
    const urlBreak = search().break ?? BREAK_PRESET_5;
    const urlNote = search().dialog === "note";
    // Comparisons use `untrack`: the effect reacts only to EXTERNAL URL
    // changes. Tracking the signals it writes makes a state-first change
    // (preset/mode/note + async syncDeepWorkUrl) retrigger it with the old search
    // and revert the selection; because syncDeepWorkUrl reads the already-reverted
    // signals, `?focus/break/mode/dialog` does not change (bug #15b / CC-16).
    if (!isRunning() && !activeSessionId()) {
      if (untrack(() => urlMode !== isBreak())) {
        setIsBreak(urlMode);
      }
      if (untrack(() => urlFocus !== focusMinutes())) {
        setFocusMinutes(urlFocus);
      }
      if (untrack(() => urlBreak !== breakMinutes())) {
        setBreakMinutes(urlBreak);
      }
      setTimeLeft((urlMode ? urlBreak : urlFocus) * SECONDS_PER_MINUTE);
    }
    if (activeSessionId() && untrack(() => urlNote !== isNoteOpen())) {
      setIsNoteOpen(urlNote);
    }
  });

  const handleSaveNote = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const sessionId = activeSessionId();
    if (!dbInstance || !sessionId) {
      showToast(t(msg`No active focus session found to associate the note`), "warning");
      return;
    }

    const text = noteText().trim();
    if (!text) {
      showToast(t(msg`Please enter a note`), "warning");
      return;
    }

    setIsSavingNote(true);
    try {
      await dbInstance.insert(feed_event).values({
        id: generateId(),
        workspace_id: workspaceId(),
        user_id: userId(),
        event_type: "status_update",
        event_data: JSON.stringify({ message: text, focus_session_id: sessionId }),
        created_at: new Date().toISOString(),
        deleted_at: null,
      });

      showToast(t(msg`Focus note saved successfully`), "success");
      setIsNoteOpen(false);
      setNoteText("");
      syncDeepWorkUrl();
    } catch (err) {
      console.error("Error saving note:", err);
      showToast(t(msg`Error saving note`), "error");
    } finally {
      setIsSavingNote(false);
    }
  };

  const initialTimeLeft = () => (isBreak() ? breakMinutes() : focusMinutes()) * SECONDS_PER_MINUTE;
  const [timeLeft, setTimeLeft] = createSignal(initialTimeLeft());
  const [isRunning, setIsRunning] = createSignal(false);

  // Query pomodoro configuration
  const pomodoroConfigQuery = createPowerSyncQuery<PomodoroConfig>({
    queryKey: () => queryKeys.chronos.deepWork.pomodoroConfig(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(pomodoro_config)
        .where(and(eq(pomodoro_config.user_id, userId()), ...livePomodoroConfigFilters()))
        .limit(1);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Sync state with active pomodoro configuration. The URL (`?focus=`/`?break=`)
  // takes priority over the pomodoro_config default: if the link specifies a
  // preset, we do not overwrite it with the database value.
  createEffect(() => {
    const config = pomodoroConfigQuery.data?.[0];
    if (config) {
      if (search().focus === undefined) {
        setFocusMinutes(config.focus_duration ?? FOCUS_PRESET_25);
      }
      if (search().break === undefined) {
        setBreakMinutes(config.short_break_duration ?? BREAK_PRESET_5);
      }

      // Update timeLeft if not running and not currently in active session
      if (!isRunning() && !activeSessionId()) {
        setTimeLeft((isBreak() ? breakMinutes() : focusMinutes()) * SECONDS_PER_MINUTE);
      }
    }
  });

  // Query active focus session from DB on mount
  const activeSessionQuery = createPowerSyncQuery<FocusSession>({
    queryKey: () => queryKeys.chronos.deepWork.activeSession(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(focus_session)
        .where(
          and(
            eq(focus_session.user_id, userId()),
            sql`status IN ('started', 'paused')`,
            ...liveFocusSessionFilters(),
          ),
        )
        .limit(1);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Sync state with active DB session
  createEffect(() => {
    const session = activeSessionQuery.data?.[0];
    if (session) {
      setActiveSessionId(session.id);
      setIsBreak(session.session_type === "break");
      setCurrentSegment(session.current_segment ?? 1);

      if (session.status === "started") {
        setIsRunning(true);
        if (session.start_time) {
          const elapsedMs = Date.now() - new Date(session.start_time).getTime();
          const elapsedSecs = Math.floor(elapsedMs / MS_PER_SECOND);
          const durationMins = session.session_type === "break" ? breakMinutes() : focusMinutes();
          const totalSecs = durationMins * SECONDS_PER_MINUTE;
          const remaining = Math.max(0, totalSecs - elapsedSecs);
          setTimeLeft(remaining);
        }
      } else {
        setIsRunning(false);
        // Default to preset time for the session type if paused
        const durationMins = session.session_type === "break" ? breakMinutes() : focusMinutes();
        setTimeLeft(durationMins * SECONDS_PER_MINUTE);
      }
    }
  });

  // Query completed sessions today for energy estimation
  const sessionsToday = createPowerSyncQuery<FocusSession>({
    queryKey: () => queryKeys.chronos.deepWork.sessions(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(focus_session)
        .where(
          and(
            eq(focus_session.user_id, userId()),
            eq(focus_session.status, "completed"),
            sql`created_at >= ${new Date(Date.now() - RETROSPECTIVE_HOURS * MINUTES_IN_HOUR * SECONDS_PER_MINUTE * MS_PER_SECOND).toISOString()}`,
            ...liveFocusSessionFilters(),
          ),
        );
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  const focusSessionCount = createMemo(() => {
    const list = sessionsToday.data ?? [];
    return list.filter((s) => s.session_type === "focus").length;
  });

  const breakSessionCount = createMemo(() => {
    const list = sessionsToday.data ?? [];
    return list.filter((s) => s.session_type === "break").length;
  });

  // Focus capacity dynamic calculation
  const focusCapacity = createMemo(() => {
    const calculated =
      MAX_ENERGY_LEVEL -
      focusSessionCount() * FOCUS_DECREMENT +
      breakSessionCount() * BREAK_INCREMENT;
    return Math.max(MIN_FOCUS_CAPACITY, Math.min(MAX_ENERGY_LEVEL, calculated));
  });

  // Cognitive reserves dynamic calculation
  const cognitiveReserves = createMemo(() => {
    const calculated =
      MAX_ENERGY_LEVEL -
      focusSessionCount() * COGNITIVE_FOCUS_DECREMENT +
      breakSessionCount() * COGNITIVE_BREAK_INCREMENT;
    return Math.max(MIN_COGNITIVE_RESERVES, Math.min(MAX_ENERGY_LEVEL, calculated));
  });

  // Accumulated fatigue calculation
  const accumulatedFatigue = createMemo(() => {
    const calculated =
      focusSessionCount() * FATIGUE_INCREMENT - breakSessionCount() * FATIGUE_DECREMENT;
    return Math.max(0, Math.min(MAX_ENERGY_LEVEL, calculated));
  });

  async function startTimer() {
    if (isRunning()) {
      return;
    }

    setIsRunning(true);
    let currentId = activeSessionId();
    const dbInstance = drizzle();

    if (dbInstance) {
      if (currentId) {
        // Resume existing paused session
        try {
          const adjustedStartTime = new Date(
            Date.now() - (initialTimeLeft() - timeLeft()) * MS_PER_SECOND,
          ).toISOString();
          await patchRowVerified(dbInstance, focus_session, focus_session.id, currentId, {
            status: "started",
            start_time: adjustedStartTime,
          });
        } catch (err) {
          console.error("Failed to update focus session resume status", err);
          showToast(t(msg`Could not resume the session`), "error");
        }
      } else {
        currentId = generateId();
        setActiveSessionId(currentId);

        try {
          await dbInstance.insert(focus_session).values({
            id: currentId,
            user_id: userId(),
            project_id: null,
            task_id: null,
            session_type: isBreak() ? "break" : "focus",
            status: "started",
            start_time: new Date().toISOString(),
            end_time: null,
            current_segment: currentSegment(),
            total_segments: 4,
            energy_level_start: focusCapacity(),
            energy_level_end: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            deleted_at: null,
          });

          if (!isBreak()) {
            await dbInstance.insert(feed_event).values({
              id: generateId(),
              workspace_id: workspaceId(),
              user_id: userId(),
              event_type: "focus_started",
              event_data: JSON.stringify({ description: t(msg`Deep Work Session`) }),
              created_at: new Date().toISOString(),
              deleted_at: null,
            });
          }
        } catch (err) {
          console.error("Failed to insert focus session", err);
        }
      }
    }
  }

  async function completeSession() {
    const currentId = activeSessionId();
    const dbInstance = drizzle();
    if (!currentId || !dbInstance) {
      return;
    }

    try {
      const finalEnergy = isBreak()
        ? focusCapacity() + BREAK_INCREMENT
        : focusCapacity() - FOCUS_DECREMENT;
      await patchRowVerified(dbInstance, focus_session, focus_session.id, currentId, {
        status: "completed",
        end_time: new Date().toISOString(),
        energy_level_end: Math.max(MIN_FOCUS_CAPACITY, Math.min(MAX_ENERGY_LEVEL, finalEnergy)),
      });

      if (!isBreak()) {
        setCurrentSegment((prev) => (prev < 4 ? prev + 1 : 1));
        await dbInstance.insert(feed_event).values({
          id: generateId(),
          workspace_id: workspaceId(),
          user_id: userId(),
          event_type: "focus_completed",
          event_data: JSON.stringify({
            description: t(msg`Deep Work Session`),
            duration: focusMinutes(),
          }),
          created_at: new Date().toISOString(),
          deleted_at: null,
        });
      }

      showToast(
        isBreak() ? t(msg`Break completed!`) : t(msg`Deep focus block completed!`),
        "success",
      );
    } catch (err) {
      console.error("Failed to complete focus session", err);
      showToast(t(msg`Could not save the session — progress may be lost`), "error");
    } finally {
      // The counter has reached 0 and the visual session MUST close even if the
      // write failed: leaving the timer stuck because of a sync error would be
      // worse than losing this session's energy record.
      setActiveSessionId(null);
      setIsBreak((prev) => !prev);
      setTimeLeft(initialTimeLeft());
    }
  }

  async function pauseTimer() {
    if (!isRunning()) {
      return;
    }
    setIsRunning(false);

    const currentId = activeSessionId();
    const dbInstance = drizzle();
    if (currentId && dbInstance) {
      try {
        await patchRowVerified(dbInstance, focus_session, focus_session.id, currentId, {
          status: "paused",
        });
      } catch (err) {
        console.error("Failed to pause focus session in SQLite", err);
        showToast(t(msg`Could not pause the session`), "error");
      }
    }
  }

  async function resetTimer() {
    setIsRunning(false);

    const currentId = activeSessionId();
    const dbInstance = drizzle();
    if (currentId && dbInstance) {
      try {
        await patchRowVerified(dbInstance, focus_session, focus_session.id, currentId, {
          status: "failed",
          end_time: new Date().toISOString(),
        });
      } catch (err) {
        console.error("Failed to cancel focus session in SQLite", err);
        showToast(t(msg`Could not cancel the session`), "error");
      }
      // It is ALWAYS cleared, even if the write failed: the user already requested
      // a reset, and a timer that does not react is worse than a `status`
      // left unconfirmed (it does not represent billable data).
      setActiveSessionId(null);
    }

    setTimeLeft(initialTimeLeft());
  }

  const handleResetClick = () => {
    if (isRunning() || activeSessionId()) {
      setShowAbortModal(true);
    } else {
      void resetTimer();
    }
  };

  // Timer interval tick
  createEffect(() => {
    let timerInterval: ReturnType<typeof setInterval> | null = null;
    if (isRunning() && timeLeft() > 0) {
      timerInterval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            void completeSession();
            return 0;
          }
          return prev - 1;
        });
      }, TIMER_INTERVAL_MS);
    }

    onCleanup(() => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    });
  });

  return (
    <PageContainer
      maxWidth="5xl"
      class="relative space-y-8 text-white z-10"
      containerClass="relative bg-[#000000] flex flex-col min-h-full pb-[calc(5.5rem+var(--safe-area-inset-bottom))]"
    >
      {/* Background Ambient Auras */}
      <div class="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div class="absolute top-[15%] left-[20%] w-[350px] h-[350px] rounded-full bg-[#ebb2ff]/10 blur-[80px] animate-breathe" />
        <div class="absolute bottom-[20%] right-[15%] w-[450px] h-[450px] rounded-full bg-[#ebb2ff]/5 blur-[80px] animate-rotate-slow" />
      </div>

      <PageHeader
        title={t(msg`Deep Work Mode`)}
        description={t(msg`Immersive distraction-free environment to optimize flow.`)}
        icon={Brain}
        class="border-b border-[#ebb2ff]/20 pb-4 relative z-10"
      />

      <div class="flex flex-col lg:flex-row items-center justify-center gap-16 py-8 relative z-10">
        {/* Circular Timer Display */}
        <div class="flex flex-col items-center space-y-6">
          <div class="relative w-80 h-80 flex items-center justify-center">
            {/* SVG Progress Circle */}
            <svg class="w-full h-full transform -rotate-90" viewBox="0 0 320 320">
              <circle
                cx="160"
                cy="160"
                r="148"
                class="stroke-white/5"
                stroke-width="8"
                fill="transparent"
              />
              <circle
                cx="160"
                cy="160"
                r="148"
                class="stroke-[#ebb2ff] transition-[stroke-dashoffset] duration-300"
                style={{
                  filter: "drop-shadow(0 0 8px rgba(235, 178, 255, 0.4))",
                  "stroke-linecap": "round",
                }}
                stroke-width="8"
                fill="transparent"
                stroke-dasharray="929"
                stroke-dashoffset={(() => {
                  const circ = 929;
                  const fraction = timeLeft() / initialTimeLeft();
                  return circ - fraction * circ;
                })()}
              />
            </svg>

            {/* Time readout */}
            <div class="absolute flex flex-col items-center justify-center text-center">
              <span class="text-[64px] leading-[72px] font-bold font-mono tracking-[-0.04em] tabular-nums text-primary drop-shadow-[0_0_25px_rgba(175,198,255,0.4)]">
                {formatMinutesSeconds(timeLeft())}
              </span>
              <span class="text-[9px] font-bold uppercase tracking-widest text-[#ebb2ff] mt-1 font-mono">
                {isRunning() ? t(msg`FOCUS ACTIVE`) : t(msg`PAUSED`)}
              </span>
            </div>
          </div>

          {/* Project Badge pill — glassmorphic, matching spec */}
          <div class="mt-6 px-4 py-2 rounded-full bg-[#121212]/60 border border-white/10 backdrop-blur-md flex items-center gap-2.5 shadow-lg">
            <span class="w-2.5 h-2.5 rounded-full bg-[#ebb2ff] shadow-[0_0_8px_rgba(235,178,255,0.6)]" />
            <span class="font-mono text-[13px] tracking-wide text-white/70">
              {isRunning() ? t(msg`Deep Work Session`) : t(msg`No active session`)}
            </span>
          </div>

          {/* Segment Progress Pills */}
          <div class="flex items-center gap-3">
            <span class="text-xs font-mono text-muted-foreground mr-1">
              {t(msg`Segment {current} / 4`, { current: currentSegment() })}
            </span>
            <div class="flex items-center gap-1.5">
              <For each={POMODORO_SEGMENTS}>
                {(seg) => {
                  const isCompleted = () => seg < currentSegment();
                  const isActive = () => seg === currentSegment();
                  return (
                    <div
                      class={cn(
                        "h-2 w-8 rounded-full transition-colors duration-300 relative overflow-hidden",
                        isCompleted() && "bg-[#ebb2ff]",
                        isActive() && "bg-[#ebb2ff]/20 ring-1 ring-[#ebb2ff]",
                        !isCompleted() && !isActive() && "bg-white/10",
                      )}
                    >
                      <Show when={isActive() && isRunning()}>
                        <div class="absolute inset-0 bg-[#ebb2ff] animate-pulse" />
                      </Show>
                    </div>
                  );
                }}
              </For>
            </div>
          </div>

          {/* Controls */}
          <div class="flex items-center gap-4">
            <Button
              onClick={() => {
                if (isRunning()) {
                  void pauseTimer();
                } else {
                  void startTimer();
                }
              }}
              disabled={!drizzle()}
              class="w-32 bg-[#ebb2ff] text-black hover:bg-[#ebb2ff]/90 font-bold flex items-center justify-center gap-2 rounded-full cursor-pointer animate-fade-in shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] active:scale-[0.98] transition-transform"
            >
              {isRunning() ? (
                <>
                  <Pause class="h-4 w-4" /> {t(msg`Pause`)}
                </>
              ) : (
                <>
                  <Play class="h-4 w-4" /> {t(msg`Focus`)}
                </>
              )}
            </Button>

            {/* Note Button, visible only when a focus session is active */}
            <Show when={activeSessionId() !== null}>
              <Button
                onClick={() => {
                  setIsNoteOpen(true);
                  syncDeepWorkUrl();
                }}
                variant="outline"
                class="border-white/10 hover:bg-white/5 text-white rounded-full px-4 h-10 flex items-center gap-2 cursor-pointer font-mono text-xs uppercase"
              >
                {t(msg`Note`)}
              </Button>
            </Show>

            <Button
              onClick={handleResetClick}
              variant="outline"
              size="icon"
              aria-label={t(msg`Reset timer`)}
              class="border-white/10 hover:bg-white/5 text-white rounded-full h-10 w-10 flex items-center justify-center cursor-pointer"
            >
              <RotateCcw class="h-4 w-4" />
            </Button>
          </div>

          {/* Configure Duration & Presets */}
          <div class="bg-[#121212] border border-white/10 rounded-[24px] p-6 space-y-4 max-w-sm w-full relative z-10 backdrop-blur-md">
            <div class="flex gap-2 bg-black/40 p-1 rounded-full border border-white/5">
              <button
                type="button"
                onClick={() => {
                  if (!isRunning()) {
                    setIsBreak(false);
                    setTimeLeft(focusMinutes() * SECONDS_PER_MINUTE);
                    syncDeepWorkUrl();
                  }
                }}
                class={cn(
                  "flex-1 py-1.5 text-xs font-bold rounded-full cursor-pointer transition-[color,background-color] duration-200 will-change-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ebb2ff]/50 disabled:active:scale-100",
                  isBreak() ? "text-muted-foreground hover:text-white" : "bg-[#ebb2ff] text-black",
                )}
                disabled={isRunning()}
              >
                {t(msg`Focus Session`)}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isRunning()) {
                    setIsBreak(true);
                    setTimeLeft(breakMinutes() * SECONDS_PER_MINUTE);
                    syncDeepWorkUrl();
                  }
                }}
                class={cn(
                  "flex-1 py-1.5 text-xs font-bold rounded-full cursor-pointer transition-[color,background-color] duration-200 will-change-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ebb2ff]/50 disabled:active:scale-100",
                  isBreak() ? "bg-[#ebb2ff] text-black" : "text-muted-foreground hover:text-white",
                )}
                disabled={isRunning()}
              >
                {t(msg`Break Time`)}
              </button>
            </div>

            <div class="flex flex-col space-y-4">
              <div>
                <span class="text-xs font-bold text-muted-foreground uppercase tracking-wider font-mono">
                  {t(msg`Focus Preset`)}
                </span>
                <div class="flex gap-2 mt-1.5">
                  <For each={FOCUS_PRESET_MINUTES}>
                    {(mins) => (
                      <button
                        type="button"
                        onClick={() => {
                          if (!isRunning()) {
                            setFocusMinutes(mins);
                            if (!isBreak()) {
                              setTimeLeft(mins * SECONDS_PER_MINUTE);
                            }
                            syncDeepWorkUrl();
                          }
                        }}
                        class={cn(
                          "px-3 py-1.5 text-xs font-bold rounded-[12px] border cursor-pointer flex-1 text-center font-mono transition-[color,background-color,border-color] duration-200 will-change-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ebb2ff]/50 disabled:active:scale-100",
                          focusMinutes() === mins && !isBreak()
                            ? "bg-[#ebb2ff]/20 border-[#ebb2ff] text-[#ebb2ff]"
                            : "border-white/10 hover:bg-white/5 text-muted-foreground hover:text-white",
                        )}
                        disabled={isRunning()}
                      >
                        {mins}m
                      </button>
                    )}
                  </For>
                </div>
              </div>

              <div>
                <span class="text-xs font-bold text-muted-foreground uppercase tracking-wider font-mono">
                  {t(msg`Break Preset`)}
                </span>
                <div class="flex gap-2 mt-1.5">
                  <For each={BREAK_PRESET_MINUTES}>
                    {(mins) => (
                      <button
                        type="button"
                        onClick={() => {
                          if (!isRunning()) {
                            setBreakMinutes(mins);
                            if (isBreak()) {
                              setTimeLeft(mins * SECONDS_PER_MINUTE);
                            }
                            syncDeepWorkUrl();
                          }
                        }}
                        class={cn(
                          "px-3 py-1.5 text-xs font-bold rounded-[12px] border cursor-pointer flex-1 text-center font-mono transition-[color,background-color,border-color] duration-200 will-change-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ebb2ff]/50 disabled:active:scale-100",
                          breakMinutes() === mins && isBreak()
                            ? "bg-[#ebb2ff]/20 border-[#ebb2ff] text-[#ebb2ff]"
                            : "border-white/10 hover:bg-white/5 text-muted-foreground hover:text-white",
                        )}
                        disabled={isRunning()}
                      >
                        {mins}m
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Nivel de Energía Widget */}
        <div class="bg-[#121212] border border-white/10 rounded-[24px] p-6 space-y-5 max-w-sm w-full relative z-10 backdrop-blur-md">
          <div class="flex items-center gap-2.5 text-sm font-bold text-[#ebb2ff] uppercase tracking-wider font-mono">
            <Battery class="h-5 w-5" />
            <span>{t(msg`Fatigue & Energy Level`)}</span>
          </div>

          <div class="space-y-4">
            <div class="space-y-1.5">
              <div class="flex justify-between text-xs font-semibold text-muted-foreground font-mono">
                <span>{t(msg`Focus Capacity`)}</span>
                <span class="text-white">{focusCapacity()}%</span>
              </div>
              <div class="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  class="h-full bg-gradient-to-r from-[#ceffdf] via-[#afc6ff] to-[#ebb2ff] rounded-full transition-[width] duration-500"
                  style={{ width: `${focusCapacity()}%` }}
                />
              </div>
            </div>

            <div class="space-y-1.5">
              <div class="flex justify-between text-xs font-semibold text-muted-foreground font-mono">
                <span>{t(msg`Cognitive Reserves`)}</span>
                <span class="text-white">{cognitiveReserves()}%</span>
              </div>
              <div class="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  class="h-full bg-gradient-to-r from-[#ceffdf] via-[#afc6ff] to-[#ebb2ff] rounded-full transition-[width] duration-500"
                  style={{ width: `${cognitiveReserves()}%` }}
                />
              </div>
            </div>

            <div class="space-y-1.5">
              <div class="flex justify-between text-xs font-semibold text-muted-foreground font-mono">
                <span>{t(msg`Accumulated Fatigue`)}</span>
                <span class="text-white">{accumulatedFatigue()}%</span>
              </div>
              <div class="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  class="h-full bg-gradient-to-r from-[#ceffdf] to-[#ffb4ab] rounded-full transition-[width] duration-500"
                  style={{ width: `${accumulatedFatigue()}%` }}
                />
              </div>
            </div>
          </div>

          <div class="flex items-start gap-2 bg-white/5 rounded-xl p-3 border border-[#ebb2ff]/10 text-xs">
            <Zap class="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <span class="text-muted-foreground">
              <strong class="text-white">{t(msg`Tip:`)}</strong>{" "}
              {accumulatedFatigue() > FATIGUE_THRESHOLD ? (
                <span>
                  {t(msg`Take a {mins}-minute break now to restore focus capacity.`, {
                    mins: breakMinutes(),
                  })}
                </span>
              ) : (
                <span>
                  {t(msg`Your cognitive reserve is optimal. Block the next hour for deep focus.`)}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmDialog
        open={showAbortModal()}
        onOpenChange={setShowAbortModal}
        title={t(msg`Abort Focus Session?`)}
        description={t(
          msg`Are you sure you want to cancel the current deep focus block? Your progress for this segment will not be marked as completed.`,
        )}
        confirmLabel={t(msg`Abort`)}
        cancelLabel={t(msg`Keep Focusing`)}
        variant="destructive"
        onConfirm={() => {
          void resetTimer();
          setShowAbortModal(false);
        }}
        onCancel={() => setShowAbortModal(false)}
      />

      {/* Write Note Dialog */}
      <Dialog
        open={isNoteOpen()}
        onOpenChange={(open) => {
          setIsNoteOpen(open);
          syncDeepWorkUrl();
        }}
        contentClass="bg-[#121212] border border-white/10 rounded-[24px] p-0 overflow-hidden max-w-md"
      >
        <div class="p-6 text-white">
          <DialogHeader showClose border={false} class="p-0 mb-4 text-white">
            <DialogTitle class="text-xl font-bold font-mono">
              {t(msg`Write Focus Note`)}
            </DialogTitle>
            <DialogDescription class="text-xs text-white/60 font-mono mt-1">
              Notes are linked to your active session and appear in your history and team feed.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveNote} class="space-y-4">
            <div class="space-y-1.5">
              <label
                for="deep-work-note-text"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                What are you accomplishing in this block?
              </label>
              <textarea
                id="deep-work-note-text"
                rows="4"
                placeholder={t(
                  msg`e.g. Refactoring the sync connectors and cleaning type warnings...`,
                )}
                value={noteText()}
                onInput={(e) => setNoteText(e.currentTarget.value)}
                class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 font-mono resize-none"
              />
            </div>

            <div class="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsNoteOpen(false);
                  syncDeepWorkUrl();
                }}
                class="px-5 py-2.5 rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer border border-white/5 hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSavingNote() || !drizzle()}
                class="px-6 py-2.5 bg-[#ebb2ff] text-black rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:bg-[#ebb2ff]/95"
              >
                {isSavingNote() ? t(msg`Saving...`) : t(msg`Save Note`)}
              </Button>
            </div>
          </form>
        </div>
      </Dialog>
    </PageContainer>
  );
}

interface DeepWorkSearch {
  mode?: "break";
  focus?: number;
  break?: number;
  dialog?: "note";
}

/** Only valid focus/break presets are accepted (defense against manipulated URLs). */
const toFocusPreset = (raw: unknown): number | undefined => {
  const n = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : Number.NaN;
  return FOCUS_PRESET_MINUTES.includes(n) ? n : undefined;
};
const toBreakPreset = (raw: unknown): number | undefined => {
  const n = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : Number.NaN;
  return BREAK_PRESET_MINUTES.includes(n) ? n : undefined;
};

export const chronosDeepWorkRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/deep-work",
  validateSearch: (raw: Record<string, unknown>): DeepWorkSearch => ({
    mode: raw.mode === "break" ? "break" : undefined,
    focus: toFocusPreset(raw.focus),
    break: toBreakPreset(raw.break),
    dialog: raw.dialog === "note" ? "note" : undefined,
  }),
  component: DeepWorkPage,
});
